import type {
  GridReadyEvent,
  RowClickedEvent,
  StateUpdatedEvent
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import LoadingIndicator from '@/components/LoadingIndicator';
import {
  qbFiltersAtom,
  qbTimeFilterAtom
} from '@/components/QueryBuilder/QueryBuilder.hooks';
import { DEFAULT_TIME_FILTER } from '@/components/TimeFilter';
import { DEFAULT_GRID_PAGE_SIZE } from '@/constants/common';
import useGridTheme from '@/hooks/useGridTheme';
import { useHandleError } from '@/hooks/useHandleError';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import {
  getServerSideDatasource,
  ticketsGridApiAtom
} from '@/pages/Tickets/Tickets.store';
import { userIsInternalAtom } from '@/store/user.store';
import { gridGetStoredState, gridSetStoreState } from '@/utils/Grid.utils';
import { loadingCellRenderer } from '@/utils/GridRenderer.utils';

import {
  getTicketColumnDefs,
  TICKETS_TABLE_DEFAULT_SORT_MODEL
} from './TicketsTable.constants';
import useStyles from './TicketTable.styles';

const TicketsTableV2 = () => {
  const gridTheme = useGridTheme();
  const navigate = useNavigate();
  const { classes } = useStyles();

  const selectedOrganization = useAtomValue(selectedOrganizationAtom);
  const ticketFilters = useAtomValue(qbFiltersAtom);
  const ticketTimeFilter = useAtomValue(qbTimeFilterAtom);
  const userIsInternal = useAtomValue(userIsInternalAtom);
  const [ticketsGridApi, setTicketsGridApi] = useAtom(ticketsGridApiAtom);

  const handleError = useHandleError();
  const [isFetching, setIsFetching] = useState(false);

  const { initialState, pageSize } = useMemo(() => {
    const storedState = gridGetStoredState(
      'TicketsTable',
      TICKETS_TABLE_DEFAULT_SORT_MODEL
    );

    return {
      initialState: storedState,
      pageSize: storedState.pagination?.pageSize ?? DEFAULT_GRID_PAGE_SIZE
    };
  }, []);

  const cacheBlockSize = pageSize * 5;

  const filtersHydrated = ticketTimeFilter !== null;

  const ticketFiltersKey = JSON.stringify(ticketFilters);
  const ticketTimeFilterKey = JSON.stringify(ticketTimeFilter);
  const memoizedDatasource = useMemo(
    () =>
      getServerSideDatasource(
        selectedOrganization.label as string,
        ticketFilters,
        ticketTimeFilter || DEFAULT_TIME_FILTER,
        handleError,
        setIsFetching
      ),
    // Serialize objects so useMemo compares by value, not reference.
    // ticketFilters and ticketTimeFilter are arrays/objects that may get new
    // references on every render even when the content hasn't changed.
    [selectedOrganization.label, ticketFiltersKey, ticketTimeFilterKey]
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      setTicketsGridApi(params.api);
    },
    [setTicketsGridApi]
  );

  useEffect(() => {
    if (!filtersHydrated) return;
    if (ticketsGridApi && !ticketsGridApi.isDestroyed()) {
      ticketsGridApi.setGridOption('serverSideDatasource', memoizedDatasource);
    }
  }, [memoizedDatasource, ticketsGridApi, filtersHydrated]);

  const columnDefs = useMemo(() => {
    const columns = getTicketColumnDefs(userIsInternal).map((col) => {
      if (col.field === 'organization_name') {
        return { ...col, hide: selectedOrganization.id > 0 };
      }
      return col;
    });
    return columns;
  }, [selectedOrganization, userIsInternal]);

  const handleRowClick = (event: RowClickedEvent) => {
    const mouseEvent = event.event as MouseEvent | undefined;
    if (mouseEvent?.ctrlKey || mouseEvent?.metaKey) {
      // Open in new tab if Ctrl, or Command key is pressed
      const newWindow = window.open(
        `/ticket/${event.data.id}`,
        '_blank',
        'noopener,noreferrer'
      );
      if (newWindow) newWindow.opener = null;
      return;
    }
    navigate(`/ticket/${event.data.id}`);
  };

  const handleStateUpdate = useCallback(
    (event: StateUpdatedEvent) => gridSetStoreState(event, 'TicketsTable'),
    []
  );

  const gridOptions = useMemo(() => ({ initialState }), [initialState]);

  return (
    <div
      className={classes.tableLoadingDisplay}
      style={{ height: '100%', width: '100%' }}
    >
      <LoadingIndicator isLoading={isFetching} noBackground />
      <AgGridReact
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        rowModelType="serverSide"
        pagination
        paginationPageSize={20}
        cacheBlockSize={cacheBlockSize}
        onGridReady={onGridReady}
        theme={gridTheme}
        suppressCellFocus
        onRowClicked={(event) => {
          handleRowClick(event);
        }}
        loadingCellRenderer={loadingCellRenderer}
        onStateUpdated={handleStateUpdate}
        rowHeight={55}
        loading={false} // Needed to disable default loading overlay, we use a custom one
      />
    </div>
  );
};

export default TicketsTableV2;
